/**
 * Settings panel component - App configuration
 */

import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { databaseService } from '../services/database.js';
import { notificationService } from '../services/notifications.js';
import { audioService } from '../services/audio.js';
import SyncService from '../services/sync.js';
import type { AppState } from '../models/check-in.js';

@customElement('settings-panel')
export class SettingsPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .container {
      max-width: 100%;
      padding: 0;
    }

    .title {
      font-size: 0.85rem;
      font-weight: 400;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
      padding: 0.25rem 0.5rem;
      border-bottom: 1px solid var(--color-border);
    }

    .section {
      background-color: var(--bg-secondary);
      border-radius: 0;
      padding: 0.5rem;
      margin-bottom: 0.25rem;
      border: 1px solid var(--color-border);
    }

    .section-title {
      font-size: 0.8rem;
      font-weight: 400;
      color: var(--text-secondary);
      margin-bottom: 0.25rem;
    }

    .setting-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.25rem 0;
      border-bottom: 1px solid var(--color-border);
    }

    .setting-row:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    .setting-row:first-child {
      padding-top: 0;
    }

    .setting-info {
      flex: 1;
    }

    .setting-label {
      font-weight: 400;
      color: var(--text-primary);
      margin-bottom: 0;
      font-size: 0.85rem;
    }

    .setting-description {
      font-size: 0.7rem;
      color: var(--text-tertiary);
    }

    .toggle {
      position: relative;
      width: 32px;
      height: 16px;
    }

    .toggle input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    .toggle-slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--bg-tertiary);
      border-radius: 0;
      transition: none;
      border: 1px solid var(--color-border);
    }

    .toggle-slider:before {
      position: absolute;
      content: "";
      height: 12px;
      width: 12px;
      left: 2px;
      bottom: 1px;
      background-color: var(--text-tertiary);
      border-radius: 0;
      transition: transform 0.1s;
    }

    .toggle input:checked + .toggle-slider {
      background-color: var(--color-primary);
    }

    .toggle input:checked + .toggle-slider:before {
      transform: translateX(16px);
      background-color: white;
    }

    .input-group {
      margin-top: 0.5rem;
    }

    .input-label {
      display: block;
      font-weight: 400;
      color: var(--text-secondary);
      margin-bottom: 0.25rem;
      font-size: 0.75rem;
    }

    .input {
      width: 100%;
      padding: 0.25rem 0.5rem;
      background-color: var(--bg-primary);
      color: var(--text-primary);
      border: 1px solid var(--color-border);
      border-radius: 0;
      font-family: inherit;
      font-size: 0.85rem;
    }

    .input:focus {
      border-color: var(--color-primary);
      outline: none;
    }

    .button {
      padding: 0.25rem 0.5rem;
      background-color: var(--color-primary);
      color: white;
      border: none;
      border-radius: 0;
      font-weight: 400;
      cursor: pointer;
      transition: none;
      font-size: 0.85rem;
    }

    .button:hover {
      background-color: var(--color-primary-hover);
    }

    .button-danger {
      background-color: var(--color-error);
    }

    .button-danger:hover {
      background-color: #dc2626;
    }

    .message {
      margin-top: 0.25rem;
      padding: 0.25rem 0.5rem;
      border-radius: 0;
      font-size: 0.75rem;
    }

    .message.success {
      background-color: var(--color-success);
      color: white;
    }

    .message.error {
      background-color: var(--color-error);
      color: white;
    }
  `;

  @state() private appState: AppState | null = null;
  @state() private syncUrl = '';
  @state() private syncMessage = '';
  @state() private syncError = false;

  private syncService: SyncService | null = null;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadSettings();
  }

  private async loadSettings() {
    try {
      this.appState = await databaseService.getAppState();
      this.syncUrl = this.appState.settings.syncUrl || '';

      if (this.appState.settings.syncUrl) {
        this.syncService = new SyncService(databaseService.getDB());
        await this.syncService.configureSyncUrl(this.appState.settings.syncUrl);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }

  private async handleSoundToggle() {
    if (!this.appState) return;

    const newValue = !this.appState.settings.soundEnabled;
    await databaseService.updateAppState({
      settings: {
        ...this.appState.settings,
        soundEnabled: newValue
      }
    });

    this.appState = {
      ...this.appState,
      settings: {
        ...this.appState.settings,
        soundEnabled: newValue
      }
    };
  }

  private async handleVolumeChange(e: Event) {
    if (!this.appState) return;

    const volume = parseFloat((e.target as HTMLInputElement).value);
    await databaseService.updateAppState({
      settings: {
        ...this.appState.settings,
        soundVolume: volume
      }
    });

    this.appState = {
      ...this.appState,
      settings: {
        ...this.appState.settings,
        soundVolume: volume
      }
    };

    // Apply to audio service immediately
    audioService.setVolume(volume);
  }

  private async handleNotificationsToggle() {
    if (!this.appState) return;

    const newValue = !this.appState.settings.notificationsEnabled;

    if (newValue) {
      const granted = await notificationService.requestPermission();
      if (!granted) {
        alert('Notification permission denied. Please enable it in your browser settings.');
        return;
      }
    }

    await databaseService.updateAppState({
      settings: {
        ...this.appState.settings,
        notificationsEnabled: newValue
      }
    });

    this.appState = {
      ...this.appState,
      settings: {
        ...this.appState.settings,
        notificationsEnabled: newValue
      }
    };
  }

  private async handleSyncUrlSave() {
    this.syncMessage = '';
    this.syncError = false;

    try {
      if (this.syncUrl.trim()) {
        // Test connection
        if (!this.syncService) {
          this.syncService = new SyncService(databaseService.getDB());
        }
        await this.syncService.configureSyncUrl(this.syncUrl);

        // Save to settings
        await databaseService.updateAppState({
          settings: {
            ...this.appState!.settings,
            syncUrl: this.syncUrl
          }
        });

        this.syncMessage = 'Sync configured successfully!';
      } else {
        // Clear sync
        if (this.syncService) {
          this.syncService.stopSync();
          this.syncService = null;
        }

        await databaseService.updateAppState({
          settings: {
            ...this.appState!.settings,
            syncUrl: null
          }
        });

        this.syncMessage = 'Sync disabled';
      }
    } catch (error) {
      console.error('Failed to configure sync:', error);
      this.syncMessage = `Failed to connect: ${(error as Error).message}`;
      this.syncError = true;
    }
  }

  private async handleClearData() {
    const confirmed = confirm(
      'Are you sure you want to clear all local data? This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      await databaseService.destroy();
      window.location.reload();
    } catch (error) {
      console.error('Failed to clear data:', error);
      alert('Failed to clear data. Please try again.');
    }
  }

  render() {
    if (!this.appState) {
      return html`<div class="container">loading...</div>`;
    }

    const { settings } = this.appState;

    return html`
      <div class="container">
        <div class="title">settings</div>

        <div class="section">
          <div class="section-title">alerts</div>

          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">sound</div>
              <div class="setting-description">
                play sound at period boundaries
              </div>
            </div>
            <label class="toggle">
              <input
                type="checkbox"
                .checked=${settings.soundEnabled}
                @change=${this.handleSoundToggle}
              />
              <span class="toggle-slider"></span>
            </label>
          </div>

          ${settings.soundEnabled ? html`
            <div class="setting-row">
              <div class="setting-info">
                <div class="setting-label">volume</div>
                <div class="setting-description">
                  ${Math.round(settings.soundVolume * 100)}%
                </div>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                .value=${settings.soundVolume.toString()}
                @input=${this.handleVolumeChange}
                style="width: 100px;"
              />
            </div>
          ` : ''}

          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">notifications</div>
              <div class="setting-description">
                browser notifications at boundaries
              </div>
            </div>
            <label class="toggle">
              <input
                type="checkbox"
                .checked=${settings.notificationsEnabled}
                @change=${this.handleNotificationsToggle}
              />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div class="section">
          <div class="section-title">sync</div>

          <div class="input-group">
            <label class="input-label" for="sync-url">
              couchdb url
            </label>
            <input
              id="sync-url"
              class="input"
              type="url"
              .value=${this.syncUrl}
              @input=${(e: Event) => {
                this.syncUrl = (e.target as HTMLInputElement).value;
              }}
              placeholder="https://user:pass@host:5984/full-life"
            />
          </div>

          <button class="button" @click=${this.handleSyncUrlSave} style="margin-top: 0.5rem;">
            ${this.syncUrl.trim() ? 'save & connect' : 'disable'}
          </button>

          ${this.syncMessage ? html`
            <div class="message ${this.syncError ? 'error' : 'success'}">
              ${this.syncMessage}
            </div>
          ` : ''}
        </div>

        <div class="section">
          <div class="section-title">data</div>

          <div class="setting-info">
            <div class="setting-label">clear all data</div>
            <div class="setting-description">
              delete all local data (cannot undo)
            </div>
          </div>

          <button class="button button-danger" @click=${this.handleClearData} style="margin-top: 0.5rem;">
            clear all data
          </button>
        </div>
      </div>
    `;
  }
}
